import { Clock, GitBranch } from "lucide-react";

import { cn } from "@monorepo-apps/ui/src/lib/utils";

import type { CheckpointEntry } from "@/routes/_app/-timeline-v2.data";

const TONE = {
  transition: {
    accent: "oklch(0.72 0.17 190)",
    accent45: "oklch(0.72 0.17 190 / 0.45)",
    accent60: "oklch(0.72 0.17 190 / 0.6)",
    accent12: "oklch(0.72 0.17 190 / 0.12)",
    eyebrow: "Inflection",
    icon: GitBranch,
  },
  build: {
    accent: "oklch(0.78 0.16 65)",
    accent45: "oklch(0.78 0.16 65 / 0.45)",
    accent60: "oklch(0.78 0.16 65 / 0.6)",
    accent12: "oklch(0.78 0.16 65 / 0.12)",
    eyebrow: "Estimation",
    icon: Clock,
  },
} as const;

export function CheckpointDetailCard({ entry }: { entry: CheckpointEntry }) {
  const tone = TONE[entry.tone];
  return (
    <article
      data-slot="checkpoint-detail"
      className="hero-panel relative overflow-hidden rounded-xl px-6 py-6"
    >
      <InflectionMark
        eyebrow={tone.eyebrow}
        date={entry.date}
        accent60={tone.accent60}
        accent={tone.accent}
      />
      <CardHeader title={entry.title} icon={tone.icon} accent={tone.accent} />
      <OutcomeChip
        outcome={entry.outcome}
        accent={tone.accent}
        accent45={tone.accent45}
        accent12={tone.accent12}
      />
      <Bullets bullets={entry.bullets} />
    </article>
  );
}

function InflectionMark({
  eyebrow,
  date,
  accent60,
  accent,
}: {
  eyebrow: string;
  date: string;
  accent60: string;
  accent: string;
}) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <span aria-hidden className="h-px w-10" style={{ background: accent60 }} />
      <span className="font-data text-[10px] tracking-[0.24em] uppercase" style={{ color: accent }}>
        {eyebrow} · {date}
      </span>
    </div>
  );
}

function CardHeader({
  title,
  icon: Icon,
  accent,
}: {
  title: string;
  icon: typeof GitBranch;
  accent: string;
}) {
  return (
    <header className="flex items-center gap-2">
      <Icon className="h-4 w-4" style={{ color: accent }} />
      <h2 className="text-lg font-medium tracking-tight text-white">{title}</h2>
    </header>
  );
}

function OutcomeChip({
  outcome,
  accent,
  accent45,
  accent12,
}: {
  outcome: string;
  accent: string;
  accent45: string;
  accent12: string;
}) {
  return (
    <div
      className="mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5"
      style={{ borderColor: accent45, background: accent12 }}
    >
      <span
        aria-hidden
        className={cn("animate-status-pulse inline-block h-1.5 w-1.5 rounded-full")}
        style={{ background: accent }}
      />
      <span className="font-data text-[11px] tracking-wide text-white">{outcome}</span>
    </div>
  );
}

function Bullets({ bullets }: { bullets: string[] }) {
  return (
    <ul className="mt-5 flex flex-col gap-1.5">
      {bullets.map((b) => (
        <Bullet key={b} text={b} />
      ))}
    </ul>
  );
}

function Bullet({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2 text-[13px] leading-relaxed text-white/85">
      <span
        aria-hidden
        className="mt-[7px] inline-block h-1 w-1 shrink-0 rounded-full bg-white/40"
      />
      <span>{text}</span>
    </li>
  );
}
